import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '../helpers/telemetry.js';

let currentSession = null;
let currentSessionId = null;

const template = {
    client: null,
    salon: null,

    type: 'appointment',
    employee: null,
    treatment: null,
    date: null,
    time: null,
    customer: {
        firstname: null,
        lastname: null,
        email: null,
        telephone: null
    },

    preset: {
        treatment: null
    },

    params: {},

    id: null,
    token: null,
    status: 'started',
    origin: null,
    consent: false,
    validate: 'late',
    chatAssisted: false
};

/**
 * Whether this session has been assisted by the chat agent. Once the customer
 * has used the chat assistant (see chat `send()`), the flag is set permanently
 * and reported as the `assistant` property on every call that identifies the
 * booking source, so the backend can distinguish chat-assisted bookings.
 */
function isChatAssisted() {
    return !!currentSession?.chatAssisted;
}



/**
 * Load an existing session by ID
 * Returns the $state session proxy if exists, false if not found
 */
function loadSession(sessionId) {
    if (currentSessionId === sessionId && currentSession) {
        return currentSession;
    }
    
    const storageKey = `session_${sessionId}`;
    const stored = sessionStorage.getItem(storageKey);
    
    if (!stored) {
        return false;
    }
    
    // Load session data and create reactive state
    const sessionData = JSON.parse(stored);
    const value = Object.assign({}, template, sessionData);
    const session = $state(value);
    
    // Cache the session
    currentSession = session;
    currentSessionId = sessionId;

    // Link session to Sentry for searchable error tracking
    Sentry.setTag('session.id', sessionId);
    Sentry.setTag('session.client', session.client);
    Sentry.setTag('session.salon', session.salon);

    // Setup auto-save effect
    $effect.root(() => {
        $effect(() => {
            const snapshot = $state.snapshot(session);
            sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
        });
    });
    
    return session;
}

/**
 * Initialize a new session with default data
 * Returns the new session ID
 */
function initializeSession(defaultData = {}) {
    const sessionId = uuidv4();
    const storageKey = `session_${sessionId}`;

    // Create session data with defaults
    const sessionData = Object.assign({}, template, defaultData);

    // Capture URL parameters before routing rewrites the URL
    sessionData.params = Object.fromEntries(new URLSearchParams(window.location.search));
    
    try {
        // Store in sessionStorage
        sessionStorage.setItem(storageKey, JSON.stringify(sessionData));
    } catch (error) {
        console.error('Failed to store session data:', error);
    }
    
    // Create reactive state and cache it
    const session = $state(sessionData);
    currentSession = session;
    currentSessionId = sessionId;

    // Link session to Sentry for searchable error tracking
    Sentry.setTag('session.id', sessionId);
    if (sessionData.client) Sentry.setTag('session.client', sessionData.client);
    if (sessionData.salon) Sentry.setTag('session.salon', sessionData.salon);

    // Setup auto-save effect
    $effect.root(() => {
        $effect(() => {
            const snapshot = $state.snapshot(session);
            sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
        });
    });
    
    return sessionId;
}

export { loadSession, initializeSession, isChatAssisted, currentSession, currentSessionId };